// external
import React, { createContext, useState, useContext, useReducer } from "react";
import uniqid from "uniqid";

// api
import apiRoot from "../api/apiRoot";

// state managing/security
import { ClientContext } from "./ClientContext";
import { refreshReducer } from "../reducers/refresh-reducer";
import { aidResultsBuildReducer } from "reducers/aid-results-reducer";

export const FaoBuilderContext = createContext();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const FaoBuilderStore = ({ children }) => {
  const [refreshStatus, refreshDispatch] = useReducer(refreshReducer, {});
  const [aidResultsBuildStatus, aidResultsBuildDispatch] = useReducer(
    aidResultsBuildReducer,
    {}
  );
  const [modelConfig, setModelConfig] = useState(null);
  const { currentClientData } = useContext(ClientContext);

  const formatDate = (date, setDate) => {
    if (date !== "") {
      setDate(
        `${new Date(date).toLocaleDateString()} ${new Date(
          date
        ).toLocaleTimeString()}`
      );
    } else {
      setDate(null);
    }
  };
  const createNewModel = async () => {
    try {
      // Generate ID first
      const modelId = uniqid();

      const requestData = {
        id: modelId, // Include ID in request
        schoolKey: currentClientData.schoolKey,
        versionName: "",
        entryTerm: "",
        studentType: "",
        authorName: "",
        status: "draft",
        lastUpdatedDate: "",
        configs: {
          importConfig: "",
          regimeParams: "",
          scenario: "",
          awardingStrategy: "",
        },
      };

      // Store ID in context immediately
      setModelConfig({ id: modelId, ...requestData });
      console.log(requestData);
      // Fire and forget the creation request
      apiRoot.post("/api/fao-model", requestData).catch((error) => {
        console.error("Background save failed:", error);
        // Could add toast notification here
      });

      return { id: modelId };
    } catch (error) {
      console.error("Create model error:", error);
      throw new Error("Failed to create model");
    }
  };

  const updateModelConfig = async (configType, configId) => {
    try {
      const updateDto = {
        ...modelConfig,
        configs: {
          ...modelConfig.configs,
          [configType]: configId,
        },
      };

      // Make the API call with proper query parameters
      const response = await apiRoot.patch(
        `/api/fao-model/${modelConfig.id}`,
        updateDto
      );

      if (!response || !response.data) {
        throw new Error("Invalid response from server");
      }
      // Update local state with new model config
      setModelConfig((prevConfig) => ({
        ...prevConfig,
        ...response.data,
      }));

      return response.data;
    } catch (error) {
      console.error(`Error updating ${configType} configuration:`, error);
      throw new Error(
        `Failed to update ${configType} configuration: ${error.message}`
      );
    }
  };

  const getModelConfig = async (modelId) => {
    try {
      const response = await apiRoot.get(`/api/fao-model/${modelId}`, {
        params: {
          schoolKey: currentClientData.schoolKey,
        },
      });
      setModelConfig(response.data);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch model configuration");
    }
  };

  const getExistingConfig = async ({ configType, endpoint, params = {} }) => {
    try {
      console.log("getExistingConfig called with:", {
        modelConfig: modelConfig.id,
        configType,
        endpoint,
      });
      const response = await apiRoot.get(endpoint, {
        params: params,
      });
      const config = response.data;
      console.log("Config data received:", config);
      return config;
    } catch (error) {
      console.error(`Error fetching ${configType} config:`, error);
      return {};
    }
  };

  const sortModels = (models) => {
    return models.sort((a, b) => {
      const scoreA = Object.values(a.configs).reduce(
        (sum, cur) => sum + (cur !== "" ? 1 : 0),
        0
      );
      const scoreB = Object.values(b.configs).reduce(
        (sum, cur) => sum + (cur !== "" ? 1 : 0),
        0
      );
      if (scoreA === scoreB) {
        return b.lastUpdatedDate - a.lastUpdatedDate;
      }

      return scoreB - scoreA;
    });
  };

  const fetchModels = async (
    setIsLoading,
    setError,
    setModels,
    status = ""
  ) => {
    try {
      setIsLoading(true);
      setError(null);

      if (!currentClientData?.schoolKey) {
        throw new Error("No school key available");
      }
      let apiEndpoint = "/api/fao-model";
      let params = {
        schoolKey: currentClientData.schoolKey,
      };
      if (status) {
        apiEndpoint = "/api/fao-model/status";
        params = { ...params, status: status };
      }
      const response = await apiRoot.get(apiEndpoint, {
        params: params,
      });

      if (!response || !response.data) {
        throw new Error("No data received from API");
      }
      const sortedModels = sortModels(response.data);
      setModels(sortedModels);
    } catch (err) {
      setError(err.message || "Failed to fetch models");
      console.error("Error fetching models:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const pollJobRunStatus = async (runId) => {
    const maxAttempts = 10; // 5 mins max
    let attempts = 0;

    let result = {};
    try {
      // get the run status for the import job
      let response = await apiRoot.get("/api/fao-model/run-status", {
        params: { runId: runId },
      });
      let runStatus = response.data.runStatus;
      // keep the loop running until the job is running and attemps haven't been maxed
      while (runStatus === "RUNNING" && attempts < maxAttempts) {
        // 30 second delay before another api call
        attempts++;
        await sleep(30000);

        // get the run status
        try {
          response = await apiRoot.get("/api/fao-model/run-status", {
            params: { runId: runId },
          });
          runStatus = response.data.runStatus;
          // if polling fails, throw an error
        } catch (pollError) {
          console.error(`Polling attempt ${attempts} failed:`, pollError);
          // Continue polling unless we've exceeded max attempts
          if (attempts >= maxAttempts) throw pollError;
        }
      }

      // Handle different outcomes
      // success
      if (response.data.runStatus === "SUCCESS") {
        result = { ...response.data, success: true };
      }
      // maxed out attempts and job is still running
      else if (
        attempts >= maxAttempts &&
        response.data.runStatus === "RUNNING"
      ) {
        result = {
          success: false,
          runStatus: "FAILURE",
          message: `Timeout - Monitor the job in databricks: ${runId}`,
          runId,
        };
      }
      // all other errors
      else {
        result = {
          success: false,
          runStatus: "FAILURE",
          message: response.data.message || "Databricks Job failed",
        };
      }
    } catch (error) {
      console.error("Job polling failed:", error);
      result = {
        success: false,
        message: "Error - Monitor the job in databricks: ",
        runId,
      };
    }
    return result;
  };

  return (
    <FaoBuilderContext.Provider
      value={{
        modelConfig,
        setModelConfig,
        createNewModel,
        updateModelConfig,
        getModelConfig,
        getExistingConfig,
        formatDate,
        sortModels,
        fetchModels,
        refreshDispatch,
        refreshStatus,
        pollJobRunStatus,
        aidResultsBuildStatus,
        aidResultsBuildDispatch,
      }}
    >
      {children}
    </FaoBuilderContext.Provider>
  );
};
